import { useMemo, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { AlertCircle, Bell, CheckCheck, Inbox } from 'lucide-react'
import { useNavigate } from 'react-router'

import { NotificationCategory } from '../api/create-notification'
import { markAsRead } from '../api/mark-as-read'
import { useMarkAllAsRead } from '../hooks/use-mark-all-as-read'
import { useNotifications } from '../hooks/use-notifications'
import { NotificationItem } from './notification-item'
import { NotificationSkeleton } from './notification-skeleton'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { paths } from '@/config/path'
import { useAuthenticatedUser } from '@/state/session'

type FilterType = 'all' | 'unread' | NotificationCategory

interface NotificationPopoverProps {
  error?: string
}

// Add this style for hiding scrollbars
const scrollbarHideStyle = `
  .scrollbar-hide {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
  .scrollbar-hide::-webkit-scrollbar {
    display: none;
  }
`

export function NotificationPopover({ error }: NotificationPopoverProps) {
  const { supaUser } = useAuthenticatedUser()
  const { notifications, isPending, unread: unreadCount } = useNotifications({ uid: supaUser.id || '' })
  const [filter, setFilter] = useState<FilterType>('all')
  const [isOpen, setIsOpen] = useState(false)
  const markAllAsReadMutation = useMarkAllAsRead({})
  const navigate = useNavigate()

  const filteredNotifications = useMemo(() => {
    return notifications.filter((notification) => {
      if (filter === 'all') return true
      if (filter === 'unread') return !notification.is_read
      return notification.category === filter
    })
  }, [notifications, filter])

  const handleMarkAsRead = async (id: string) => {
    await markAsRead(id)
  }

  const handleMarkAllAsRead = async () => {
    await markAllAsReadMutation.mutateAsync(supaUser.id)
  }

  const handleActionClick = ({ category, url }: { category: NotificationCategory; url: string }) => {
    setIsOpen(false)
    switch (category) {
      case 'order': {
        const orderId = url.split('/').pop() // Assuming URL ends with order ID
        if (orderId) {
          navigate(paths.app.order.getHref(orderId))
        }
        break
      }
      default:
        navigate(url)
    }
  }

  const renderContent = () => {
    const content = (() => {
      if (isPending) {
        return (
          <div className="space-y-0">
            {Array.from({ length: 4 }).map((_, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.3,
                  delay: index * 0.1,
                  ease: 'easeOut',
                }}
              >
                <NotificationSkeleton />
              </motion.div>
            ))}
          </div>
        )
      }

      if (error) {
        return (
          <div className="flex flex-col items-center justify-center py-8 px-4">
            <AlertCircle className="w-8 h-8 text-red-400 mb-2" />
            <p className="text-sm text-gray-600 text-center">{error}</p>
            <Button variant="outline" size="sm" className="mt-3 bg-transparent">
              Réessayer
            </Button>
          </div>
        )
      }

      if (filteredNotifications.length === 0) {
        return (
          <div className="flex flex-col items-center justify-center py-8 px-4">
            <Inbox className="w-8 h-8 text-gray-400 mb-2" />
            <p className="text-sm text-gray-600 text-center">{filter === 'unread' ? 'Aucune notification non lue' : 'Aucune notification pour le moment'}</p>
            <p className="text-xs text-gray-500 text-center mt-1">{filter === 'unread' ? 'Tout est à jour !' : 'Nous vous préviendrons quand quelque chose se passe'}</p>
          </div>
        )
      }

      return (
        <div className="max-h-96 overflow-y-auto">
          <AnimatePresence>
            {filteredNotifications.map((notification, index) => (
              <motion.div
                key={notification.id}
                initial={{ opacity: 0, y: 20, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -20, scale: 0.95 }}
                transition={{
                  duration: 0.3,
                  delay: index * 0.05,
                  ease: 'easeOut',
                }}
                layout
              >
                <NotificationItem notification={notification} onMarkAsRead={handleMarkAsRead} onActionClick={handleActionClick} />
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )
    })()

    return (
      <AnimatePresence mode="wait">
        <motion.div
          key={filter}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{
            duration: 0.25,
            ease: 'easeInOut',
          }}
        >
          {content}
        </motion.div>
      </AnimatePresence>
    )
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="w-5 h-5" />
          {unreadCount > 0 && (
            <Badge variant="destructive" className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs">
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-96 p-0" align="end" sideOffset={8}>
        <style dangerouslySetInnerHTML={{ __html: scrollbarHideStyle }} />
        {/* Header */}
        <div className="border-b border-gray-100">
          <div className="flex items-center justify-between px-4 py-3">
            <h3 className="font-semibold text-gray-900">Notifications</h3>
            {unreadCount > 0 && (
              <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-gray-500 hover:text-gray-700" onClick={handleMarkAllAsRead}>
                <CheckCheck className="w-3 h-3 mr-1" />
                Tout marquer comme lu
              </Button>
            )}
          </div>

          {/* Filter Tabs */}
          <div className="px-4 pb-3">
            <div className="flex gap-1 overflow-x-auto scrollbar-hide relative">
              {[
                { value: 'all', label: 'Toutes' },
                { value: 'unread', label: 'Non lues' },
                { value: 'order', label: 'Commandes' },
                { value: 'system', label: 'Système' },
                { value: 'invitation', label: 'Invitations' },
                { value: 'message', label: 'Messages' },
                { value: 'payment', label: 'Paiements' },
                { value: 'general', label: 'Général' },
              ].map((tab) => (
                <motion.button
                  key={tab.value}
                  onClick={() => setFilter(tab.value as FilterType)}
                  className={`relative px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-colors ${
                    filter === tab.value ? 'text-blue-700' : 'text-gray-600 hover:text-gray-900'
                  }`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {filter === tab.value && (
                    <motion.div
                      layoutId="activeTab"
                      className="absolute inset-0 bg-blue-100 rounded-full"
                      transition={{
                        type: 'spring',
                        stiffness: 500,
                        damping: 30,
                      }}
                    />
                  )}
                  <span className="relative z-10">
                    {tab.label}
                    {tab.value === 'unread' && unreadCount > 0 && (
                      <motion.span
                        className="ml-1 px-1.5 py-0.5 text-xs bg-blue-600 text-white rounded-full"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 0.1 }}
                      >
                        {unreadCount}
                      </motion.span>
                    )}
                  </span>
                </motion.button>
              ))}
            </div>
          </div>
        </div>

        {/* Content */}
        {renderContent()}
      </PopoverContent>
    </Popover>
  )
}
